import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Send as SendIcon,
  Restaurant as FoodIcon,
  FitnessCenter as FitnessIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  AutoAwesome as ProgramBuilderIcon,
} from '@mui/icons-material';
import { AiService } from '../services/aiService';
import type { AiStatus, AiModel } from '../services/aiService';
import AiProgramBuilder from './AiProgramBuilder';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AiAssistant: React.FC = () => {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [mode, setMode] = useState<'calories' | 'chat' | 'program-builder'>('calories');
  const [chatContext, setChatContext] = useState<'fitness' | 'nutrition'>('nutrition');

  // Calorie estimation state
  const [foodInput, setFoodInput] = useState('');
  const [calorieResult, setCalorieResult] = useState<string | null>(null);
  const [calorieLoading, setCalorieLoading] = useState(false);

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);

  // Model management
  const [pulling, setPulling] = useState(false);
  const [pullError, setPullError] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadStatus = async () => {
    try {
      setStatusLoading(true);
      const s = await AiService.getStatus();
      setStatus(s);
    } catch {
      setStatus({ status: 'unavailable', models: [] });
    } finally {
      setStatusLoading(false);
    }
  };

  const handlePullModel = async () => {
    try {
      setPulling(true);
      setPullError(null);
      await AiService.pullModel('tinyllama');
      await loadStatus();
    } catch (err: any) {
      setPullError(err.message || 'Failed to pull model');
    } finally {
      setPulling(false);
    }
  };

  const handleEstimateCalories = async () => {
    if (!foodInput.trim()) return;
    try {
      setCalorieLoading(true);
      setCalorieResult(null);
      const result = await AiService.estimateCalories(foodInput.trim());
      setCalorieResult(result.response);
    } catch (err: any) {
      setCalorieResult('Грешка: ' + (err.message || 'AI услугата не е достъпна'));
    } finally {
      setCalorieLoading(false);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    const userMessage = chatInput.trim();
    setChatInput('');

    const newUserMsg: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setChatMessages((prev) => [...prev, newUserMsg]);

    try {
      setChatLoading(true);
      const result = await AiService.chat(userMessage, chatContext);
      const aiMsg: ChatMessage = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, aiMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: 'Грешка: ' + (err.message || 'AI услугата не е достъпна'),
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, errorMsg]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (mode === 'calories') {
        handleEstimateCalories();
      } else {
        handleSendChat();
      }
    }
  };

  const formatModelSize = (size: number | null): string => {
    if (!size) return 'N/A';
    const gb = size / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = size / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const parseCalorieResponse = (response: string): React.ReactNode => {
    try {
      const jsonMatch = response.match(/\{[\s\S]*?\}/);
      if (jsonMatch) {
        const data = JSON.parse(jsonMatch[0]);
        return (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                label={`${data.calories} kcal`}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              />
              <Chip label={`Протеин: ${data.protein}g`} color="success" variant="outlined" />
              <Chip label={`Въглехидрати: ${data.carbs}g`} color="warning" variant="outlined" />
              <Chip label={`Мазнини: ${data.fat}g`} color="error" variant="outlined" />
              {data.fiber !== undefined && (
                <Chip label={`Фибри: ${data.fiber}g`} variant="outlined" />
              )}
            </Box>
            {data.summary && (
              <Typography variant="body2" color="text.secondary">
                {data.summary}
              </Typography>
            )}
          </Box>
        );
      }
    } catch {
      // Not JSON, render as text
    }
    return (
      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
        {response}
      </Typography>
    );
  };

  const hasModels = status?.models && status.models.length > 0;

  // Status bar
  const renderStatusBar = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      <Chip
        icon={<AiIcon />}
        label={statusLoading ? 'Проверка...' : status?.status === 'available' ? 'Ollama активен' : 'Ollama недостъпен'}
        color={status?.status === 'available' ? 'success' : 'error'}
        size="small"
      />
      {status?.models?.map((model: AiModel) => (
        <Chip
          key={model.name}
          label={`${model.name} (${formatModelSize(model.size)})`}
          size="small"
          variant="outlined"
        />
      ))}
      <IconButton size="small" onClick={loadStatus} disabled={statusLoading}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  // No model state
  if (!statusLoading && status?.status === 'available' && !hasModels) {
    return (
      <Box>
        {renderStatusBar()}
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AiIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Няма инсталиран AI модел
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Натиснете бутона за да инсталирате TinyLlama (~637MB) - малък модел за калории и фитнес съвети.
            </Typography>
            {pullError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pullError}
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={pulling ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handlePullModel}
              disabled={pulling}
            >
              {pulling ? 'Инсталиране...' : 'Инсталирай TinyLlama'}
            </Button>
            {pulling && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                Изтеглянето може да отнеме няколко минути...
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Unavailable state
  if (!statusLoading && status?.status !== 'available') {
    return (
      <Box>
        {renderStatusBar()}
        <Alert severity="warning">
          AI услугата (Ollama) не е достъпна. Уверете се, че Ollama контейнерът работи.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {renderStatusBar()}

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, val) => val && setMode(val)}
          size="small"
        >
          <ToggleButton value="calories">
            <FoodIcon sx={{ mr: 1 }} /> Калории
          </ToggleButton>
          <ToggleButton value="chat">
            <AiIcon sx={{ mr: 1 }} /> Чат
          </ToggleButton>
          <ToggleButton value="program-builder">
            <ProgramBuilderIcon sx={{ mr: 1 }} /> Програма
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'calories' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FoodIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Оценка на калории
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Опишете храната и AI ще оцени калориите и макросите.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="напр. 200г пилешко филе с ориз и салата..."
                value={foodInput}
                onChange={(e) => setFoodInput(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={calorieLoading}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleEstimateCalories}
                disabled={calorieLoading || !foodInput.trim()}
                sx={{ minWidth: 100 }}
              >
                {calorieLoading ? <CircularProgress size={24} /> : 'Оцени'}
              </Button>
            </Box>

            {calorieResult && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="primary">
                  Резултат:
                </Typography>
                {parseCalorieResponse(calorieResult)}
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {mode === 'chat' && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                <AiIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI Асистент
              </Typography>
              <ToggleButtonGroup
                value={chatContext}
                exclusive
                onChange={(_, val) => val && setChatContext(val)}
                size="small"
              >
                <ToggleButton value="nutrition">
                  <FoodIcon sx={{ mr: 0.5 }} fontSize="small" /> Хранене
                </ToggleButton>
                <ToggleButton value="fitness">
                  <FitnessIcon sx={{ mr: 0.5 }} fontSize="small" /> Фитнес
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Divider />

            <Box sx={{ height: 400, overflowY: 'auto', p: 2 }}>
              {chatMessages.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                  <AiIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Задайте въпрос за {chatContext === 'nutrition' ? 'хранене' : 'фитнес'}...
                  </Typography>
                </Box>
              )}
              {chatMessages.map((msg, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      maxWidth: '80%',
                      bgcolor: msg.role === 'user' ? 'primary.dark' : 'background.default',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, textAlign: 'right' }}>
                      {msg.timestamp.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Paper>
                </Box>
              ))}
              {chatLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                  <Paper sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 2 }}>
                    <CircularProgress size={20} />
                  </Paper>
                </Box>
              )}
              <div ref={chatEndRef} />
            </Box>

            <Divider />
            <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Напишете съобщение..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={chatLoading}
                size="small"
                multiline
                maxRows={3}
              />
              <IconButton
                color="primary"
                onClick={handleSendChat}
                disabled={chatLoading || !chatInput.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {mode === 'program-builder' && (
        <AiProgramBuilder />
      )}
    </Box>
  );
};

export default AiAssistant;
